import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { Heart, Rocket, Zap, Users } from 'lucide-react';
import { LanguageContext } from '../contexts/LanguageContext';
import { getHeadingFontClass } from '../utils/fontUtils';

const icons = {
  heart: Heart,
  rocket: Rocket,
  zap: Zap,
  users: Users,
};

const ValueCard = ({ titleId, descriptionId, icon, index }) => {
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);
  const Icon = icons[icon];

  return (
    <div
      className={`relative bg-card rounded-2xl p-8 shadow-card border border-border 
        animate-floatUp transition-all duration-400 hover:border-primary-300`}
      style={{ animationDelay: `${0.9 + index * 0.2}s` }}
    >
      {/* アイコンとタイトルを横並び */}
      <div className="flex items-center gap-4 mb-4">
        <div
          className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/80 to-primary 
          flex items-center justify-center flex-shrink-0"
        >
          <Icon className="w-6 h-6 text-white" strokeWidth={2} />
        </div>
        <h3
          className={`text-xl lg:text-2xl font-bold text-card-foreground ${getHeadingFontClass(locale)}`}
        >
          {intl.formatMessage({ id: titleId })}
        </h3>
      </div>
      <p className="text-muted-foreground leading-loose font-body text-base">
        {intl.formatMessage({ id: descriptionId })}
      </p>
    </div>
  );
};

export default function Value() {
  const intl = useIntl();

  const values = [
    {
      titleId: 'value.card1.title',
      descriptionId: 'value.card1.description',
      icon: 'heart',
    },
    {
      titleId: 'value.card2.title',
      descriptionId: 'value.card2.description',
      icon: 'rocket',
    },
    {
      titleId: 'value.card3.title',
      descriptionId: 'value.card3.description',
      icon: 'zap',
    },
    {
      titleId: 'value.card4.title',
      descriptionId: 'value.card4.description',
      icon: 'users',
    },
  ];

  return (
    <div className="pt-16 pb-[100px] relative bg-primary">
      {/* グリッドパターン */}
      <div className="absolute inset-0 value-section-pattern opacity-30 pointer-events-none" />

      {/* 上部の波形装飾 */}
      <div className="absolute top-0 left-0 right-0 -translate-y-[99%] pointer-events-none overflow-hidden">
        <svg
          viewBox="0 0 1440 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-8 sm:h-12 md:h-16 lg:h-20 -scale-x-100"
          preserveAspectRatio="none"
        >
          <path
            d="M0 80L48 74.7C96 69 192 59 288 53.3C384 48 480 48 576 50.7C672 53 768 59 864 61.3C960 64 1056 64 1152 58.7C1248 53 1344 43 1392 37.3L1440 32V80H1392C1344 80 1248 80 1152 80C1056 80 960 80 864 80C768 80 672 80 576 80C480 80 384 80 288 80C192 80 96 80 48 80H0Z"
            className="fill-primary"
          />
        </svg>
      </div>

      <div className="container relative z-10">
        <h2
          className="text-3xl md:text-4xl font-bold mb-4 sm:mb-8 text-white animate-scaleIn font-heading"
          style={{ animationDelay: '0.3s' }}
        >
          {intl.formatMessage({ id: 'value.title' })}
        </h2>
        <p
          className="text-lg sm:text-xl text-primary-foreground/85 mb-12 font-body animate-fadeInUp"
          style={{ animationDelay: '0.5s' }}
        >
          {intl.formatMessage({ id: 'value.subtitle' })}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {values.map((value, index) => (
            <ValueCard key={index} {...value} index={index} />
          ))}
        </div>
      </div>

      {/* 下部の波形装飾（CompanyRecruit への遷移） */}
      <div className="absolute bottom-0 left-0 right-0 translate-y-[99%] pointer-events-none z-20">
        <svg
          viewBox="0 0 1440 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-8 sm:h-12 md:h-16 lg:h-20"
          preserveAspectRatio="none"
        >
          <path
            d="M0 0L48 5.3C96 11 192 21 288 26.7C384 32 480 32 576 29.3C672 27 768 21 864 18.7C960 16 1056 16 1152 21.3C1248 27 1344 37 1392 42.7L1440 48V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0Z"
            className="fill-primary"
          />
        </svg>
      </div>
    </div>
  );
}
